import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import type { BizminerPillarInsight } from "@/types/serializers";

const BAR_COLOR = "oklch(0.55 0.18 250)"; // Account-Blau

interface CompetitiveScoreChartProps {
  pillar_insight: BizminerPillarInsight;
  category_id: string;
}

export function CompetitiveScoreChart({
  pillar_insight,
  category_id,
}: CompetitiveScoreChartProps) {
  const category = pillar_insight.analysis.categories.find(
    (c) => c.id === category_id,
  );

  if (!category || !pillar_insight.analysis) {
    return null;
  }

  // Map competitor IDs to their full details
  const getCompetitorName = (competitorId: string) => {
    const competitor = pillar_insight.competitors?.find(
      (c) => c.id === competitorId,
    );
    return competitor?.name || competitorId;
  };

  // Build chart data combining account score and competitor scores
  const chartData = [
    {
      name: pillar_insight.analysis.account_name,
      score: category.account_score,
    },
    ...pillar_insight.analysis.competitor_names.map((competitorId, index) => ({
      name: getCompetitorName(competitorId),
      score: category.competitor_scores[index],
    })),
  ];

  // Custom shape for bars
  const CustomBar = (props: any) => {
    const { x, y, width, height } = props;

    return (
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={BAR_COLOR}
        rx="2"
        ry="2"
      />
    );
  };

  return (
    <div className="space-y-4">
      <ResponsiveContainer height={350} width="100%">
        <BarChart data={chartData} barCategoryGap="20%" margin={{ bottom: 80 }}>
          <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 11, fill: "currentColor" }}
            className="text-muted-foreground"
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            domain={[0, 5]}
            tick={{ fontSize: 12, fill: "currentColor" }}
            className="text-muted-foreground"
          />
          <Bar
            dataKey="score"
            fill={BAR_COLOR}
            shape={CustomBar}
            maxBarSize={40}
          >
            <LabelList
              dataKey="score"
              position="top"
              fontSize={12}
              fontWeight={600}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
